import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import {
  BackdropForDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OnboardingStatusDots,
  SubmitButton,
} from 'components';
import { EOnboardingStep } from 'enums';
import { EmployeeForm } from 'forms';
import { IEmployee } from 'models';
import { useOnboardingContext } from './onboarding.context.hook';
import { DialogPrevNextClose } from './dialog-prev-next-close';

interface OnboardingEmployeeDialogProps {
  visible?: boolean;
}

export const OnboardingEmployeeDialog = ({ visible = false }: OnboardingEmployeeDialogProps) => {
  const theme = useTheme();
  const down_sm = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading_form, setLoadingForm] = useState<boolean>(false);
  const [loading_submit, setLoadingSubmit] = useState<boolean>(false);
  const { setVisible, employeeId, updateOnboarding } = useOnboardingContext();

  const formRef = React.useRef<any>();

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onSubmit = () => {
    formRef.current.onSubmit();
  };

  const onNext = employeeId
    ? () => {
        updateOnboarding({ status: EOnboardingStep.ONBOARDING_REVIEW });
      }
    : onSubmit;

  const onPrev = () => {
    updateOnboarding({ status: EOnboardingStep.APPROVE_DECAL });
  };

  const next = (employee?: IEmployee) => {
    const employeeId = employee?.id;

    if (employeeId) {
      updateOnboarding({
        employeeId,
        status: EOnboardingStep.ONBOARDING_REVIEW,
      });
    }
  };

  const title = 'Now assign a cleaning professional';
  const subtitle = 'Don’t worry, you can edit all of this in your Account.';
  const submitButtonLabel = down_sm ? 'Next' : 'Review steps';

  return (
    <Dialog
      onClose={onClose}
      open={visible}
      PaperProps={{ sx: { marginBottom: 12, ...(down_sm ? {} : { overflow: 'visible' }) } }}
    >
      <DialogTitle>
        <DialogPrevNextClose onPrev={onPrev} onNext={onNext} onClose={onClose} />

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Step 5 of 6
          </Typography>
        </Box>

        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h5">{title}</Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              marginTop: 1,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box paddingTop={2}>
          <EmployeeForm
            ref={formRef}
            employeeId={employeeId}
            onInit={(loading) => setLoadingForm(loading)}
            onSubmitting={(loading) => setLoadingSubmit(loading)}
            next={next}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <SubmitButton loading={loading_submit} label={submitButtonLabel} onClick={onSubmit} />
      </DialogActions>

      <Box
        sx={{
          width: '100%',
          left: 0,
          display: 'flex',
          justifyContent: 'center',
          ...(down_sm ? { paddingBottom: 6 } : { position: 'absolute', bottom: -60 }),
        }}
      >
        <OnboardingStatusDots status={EOnboardingStep.ASSIGN_EMPLOYEE} />
      </Box>

      <BackdropForDialog loading={loading_form} />
    </Dialog>
  );
};
